import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { useClickOutside, useDelayedHandler, useKeyPress } from '@/hooks';
import { LocalStorageService } from '@/services';
import { getCCPAAccess } from '@/helpers';
import { menuData } from '@/data/menuData';
import { LOCAL_STORAGE_KEYS } from '@/constants';
import { buttonMotion } from '@/animations';
import BurgerIcon from '@/public/icons/burger.svg';
import CloseIcon from '@/public/icons/close.svg';
import { MemoMenu } from '../Menu';
import styles from './MenuButton.module.scss';

// Types
interface IProps {
	headerElementId?: string;
	updateHeader?: (isShown: boolean) => void;
	classes?: {
		menu?: string;
		button?: string;
		icon?: string;
	};
}

// Animation
const menuMotion = {
	initial: 'initial',
	animate: 'animate',
	exit: 'exit',
	variants: {
		initial: {
			scale: 0,
			opacity: 0,
		},
		animate: {
			scale: 1,
			opacity: 1,
		},
		exit: {
			scale: 0,
			opacity: 0,
		},
	},
	transition: {
		duration: 0.25,
	},
};
const menuButtonMotion = {
	initial: 'initial',
	animate: 'animate',
	exit: 'exit',
	variants: {
		initial: {
			rotate: -90,
			opacity: 0,
		},
		animate: {
			rotate: 0,
			opacity: 1,
		},
		exit: {
			rotate: 90,
			opacity: 0,
		},
	},
	transition: {
		duration: 0.25,
	},
};

export const MenuButton = React.forwardRef(
	(
		{ headerElementId, classes, updateHeader }: IProps,
		ref: React.ForwardedRef<HTMLDivElement>,
	) => {
		// Destructuring
		const { menu, button, icon } = classes ?? {};

		// States
		const [openMenu, setOpenMenu] = React.useState(false);

		// Helpers
		const refMenu = React.useRef<HTMLDivElement>(null);
		const refButton = React.useRef<HTMLButtonElement>(null);
		const isCCPAUser = getCCPAAccess();
		useKeyPress('Escape', onClose, openMenu);
		useClickOutside([refMenu.current, refButton.current], onClose);
		const menuLinks = React.useMemo(() => {
			return LocalStorageService.get(
				LOCAL_STORAGE_KEYS.MENU_DATA,
				menuData,
			).filter(
				item => item.href !== '/dont-sell-my-personal-info' || isCCPAUser,
			);
		}, [isCCPAUser, openMenu]);
		// Actions
		function onClose() {
			setOpenMenu(false);
		}
		const onToggle: () => void = useDelayedHandler(() => {
			setOpenMenu(state => !state);
		}, 250);

		// Effects
		React.useEffect(() => {
			return () => {
				document.body.style.overflow = '';
			};
		}, []);
		React.useEffect(() => {
			if (updateHeader) {
				updateHeader(openMenu);
			}
		}, [openMenu]);

		return (
			<>
				<motion.div
					ref={ref}
					className={`${styles['menu']} ${menu ?? ''}`}
					// Animation
					{...menuMotion}
				>
					<motion.button
						type='button'
						ref={refButton}
						onClick={onToggle}
						aria-label={openMenu ? 'Close menu' : 'Open menu'}
						className={`
							${styles['menu__button']}
							${button ?? ''}
						`}
						// Animation
						{...buttonMotion}
						custom={0.8}
						data-e2e={openMenu ? 'close-menu-btn' : 'burger-menu-btn'}
					>
						<AnimatePresence initial={false} mode='popLayout'>
							{openMenu ? (
								<motion.div
									key='close'
									className={styles['menu__button-inner']}
									{...menuButtonMotion}
								>
									<CloseIcon
										className={`${styles['menu__icon']} ${icon ?? ''}`}
									/>
								</motion.div>
							) : (
								<motion.div
									key='open'
									className={styles['menu__button-inner']}
									{...menuButtonMotion}
								>
									<BurgerIcon
										className={`${styles['menu__icon']} ${icon ?? ''}`}
									/>
								</motion.div>
							)}
						</AnimatePresence>
					</motion.button>
				</motion.div>
				<MemoMenu
					ref={refMenu}
					data={menuLinks}
					isOpened={openMenu}
					headerElementId={headerElementId}
				/>
			</>
		);
	},
);
